import type { ActivityByUser_experimentalAnalyticsActivityByUser_activityByUser as ActivityByUserType } from '../../../queries/__types__/ActivityByUserQuery.ts';

import previewAgentsJson from './agentTypePreviewAgents.json';
import inProgressPreviewAgentsJson from './inProgressAgentTypePreviewAgents.json';
import priorityPreviewAgentsJson from './priorityAgentTypePreviewAgents.json';

export type AgentTypePreviewAgent = {
	agentType: string;
	accountId: string;
	displayName: string | null;
	views: number;
	created: number;
	updated: number;
	commentCreated: number;
	searchPerformed: number;
	relatedUserAccountId: string | null;
	relatedUserDisplayName: string | null;
	lastViewedAt: string;
};

type PreviewUser = {
	id: string;
	displayName: string;
	photos: { value: string; isPrimary: boolean }[];
	isActive: boolean;
};

export type AgentTypePreviewPageView = {
	accountType: 'AGENT';
	agentType: string;
	userProfile: PreviewUser | null;
	relatedUserAccountId: string | null;
	relatedUser: PreviewUser | null;
	confluenceUser: { permissionType: 'APP' };
	views: number;
	lastViewedAt: string;
	lastVersionViewedNumber: number;
	lastVersionViewedUrl: string;
	isEngaged: boolean;
};

type AgentTypePreviewFile = {
	agents: AgentTypePreviewAgent[];
};

const previewFile = previewAgentsJson as AgentTypePreviewFile;
const autonomousPreviewFile: AgentTypePreviewFile = {
	agents: previewFile.agents.filter((agent) => agent.accountId === agent.relatedUserAccountId),
};
const assistivePreviewFile: AgentTypePreviewFile = {
	agents: previewFile.agents.filter((agent) => agent.accountId !== agent.relatedUserAccountId),
};
const inProgressPreviewFile = inProgressPreviewAgentsJson as AgentTypePreviewFile;
const priorityPreviewFile = priorityPreviewAgentsJson as AgentTypePreviewFile;

export const getAgentTypePreviewAgents = (): AgentTypePreviewAgent[] => previewFile.agents;

export const getInProgressAgentTypePreviewAgents = (): AgentTypePreviewAgent[] =>
	inProgressPreviewFile.agents;

export const getPriorityAgentTypePreviewAgents = (): AgentTypePreviewAgent[] =>
	priorityPreviewFile.agents;

const toPreviewUser = (id: string | null, displayName: string | null): PreviewUser | null =>
	id && displayName
		? {
				id,
				displayName,
				photos: [],
				isActive: true,
			}
		: null;

const toPageViews = (file: AgentTypePreviewFile): AgentTypePreviewPageView[] =>
	file.agents.map((agent) => ({
		accountType: 'AGENT',
		agentType: agent.agentType,
		userProfile: toPreviewUser(agent.accountId, agent.displayName),
		relatedUserAccountId: agent.relatedUserAccountId,
		relatedUser: toPreviewUser(agent.relatedUserAccountId, agent.relatedUserDisplayName),
		confluenceUser: { permissionType: 'APP' },
		views: agent.views,
		lastViewedAt: agent.lastViewedAt,
		lastVersionViewedNumber: 1,
		lastVersionViewedUrl: '',
		isEngaged: false,
	}));

export const toAgentTypePreviewPageViews = (): AgentTypePreviewPageView[] =>
	toPageViews(previewFile);

export const toInProgressAgentTypePreviewPageViews = (): AgentTypePreviewPageView[] =>
	toPageViews(inProgressPreviewFile);

export const toPriorityAgentTypePreviewPageViews = (): AgentTypePreviewPageView[] =>
	toPageViews(priorityPreviewFile);

export const toInjectedAgentTypePreviewPageViews = (
	isAgentTypesEnabled: boolean,
	isPriorityTypesEnabled: boolean,
): AgentTypePreviewPageView[] => {
	if (!isAgentTypesEnabled) {
		return [];
	}

	if (isPriorityTypesEnabled) {
		return [
			...toPageViews(autonomousPreviewFile),
			...toInProgressAgentTypePreviewPageViews(),
			...toPriorityAgentTypePreviewPageViews(),
		];
	}

	return [
		...toPageViews(autonomousPreviewFile),
		...toPriorityAgentTypePreviewPageViews(),
		...toPageViews(assistivePreviewFile),
	];
};

const toActivityByUser = (file: AgentTypePreviewFile): ActivityByUserType[] =>
	file.agents.map((agent) => ({
		accountId: agent.accountId,
		accountType: 'AGENT',
		agentType: agent.agentType,
		relatedUserAccountId: agent.relatedUserAccountId,
		viewed: agent.views,
		created: agent.created,
		updated: agent.updated,
		commentCreated: agent.commentCreated,
		searchPerformed: agent.searchPerformed,
	}));

export const toAgentTypePreviewActivityByUser = (): ActivityByUserType[] =>
	toActivityByUser(previewFile);

export const toInProgressAgentTypePreviewActivityByUser = (): ActivityByUserType[] =>
	toActivityByUser(inProgressPreviewFile);

export const toPriorityAgentTypePreviewActivityByUser = (): ActivityByUserType[] =>
	toActivityByUser(priorityPreviewFile);

export const toInjectedAgentTypePreviewActivityByUser = (
	isAgentTypesEnabled: boolean,
	isPriorityTypesEnabled: boolean,
): ActivityByUserType[] => {
	if (!isAgentTypesEnabled) {
		return [];
	}

	if (isPriorityTypesEnabled) {
		return [
			...toActivityByUser(autonomousPreviewFile),
			...toInProgressAgentTypePreviewActivityByUser(),
			...toPriorityAgentTypePreviewActivityByUser(),
		];
	}

	return [
		...toActivityByUser(autonomousPreviewFile),
		...toPriorityAgentTypePreviewActivityByUser(),
		...toActivityByUser(assistivePreviewFile),
	];
};

export type AgentTypePreviewRelatedUserDetails = {
	displayName: string;
	profilePicPath: null;
};

const toRelatedUserDetails = (
	file: AgentTypePreviewFile,
): Record<string, AgentTypePreviewRelatedUserDetails> => {
	const details: Record<string, AgentTypePreviewRelatedUserDetails> = {};
	for (const agent of file.agents) {
		if (agent.relatedUserAccountId && agent.relatedUserDisplayName) {
			details[agent.relatedUserAccountId] = {
				displayName: agent.relatedUserDisplayName,
				profilePicPath: null,
			};
		}
	}
	return details;
};

export const toAgentTypePreviewRelatedUserDetails = (): Record<
	string,
	AgentTypePreviewRelatedUserDetails
> => toRelatedUserDetails(previewFile);

export const toInProgressAgentTypePreviewRelatedUserDetails = (): Record<
	string,
	AgentTypePreviewRelatedUserDetails
> => toRelatedUserDetails(inProgressPreviewFile);

export const toPriorityAgentTypePreviewRelatedUserDetails = (): Record<
	string,
	AgentTypePreviewRelatedUserDetails
> => toRelatedUserDetails(priorityPreviewFile);

export const toInjectedAgentTypePreviewRelatedUserDetails = (
	isAgentTypesEnabled: boolean,
	isPriorityTypesEnabled: boolean,
): Record<string, AgentTypePreviewRelatedUserDetails> => {
	if (!isAgentTypesEnabled) {
		return {};
	}

	if (isPriorityTypesEnabled) {
		return {
			...toPriorityAgentTypePreviewRelatedUserDetails(),
			...toInProgressAgentTypePreviewRelatedUserDetails(),
		};
	}

	return {
		...toPriorityAgentTypePreviewRelatedUserDetails(),
		...toAgentTypePreviewRelatedUserDetails(),
	};
};
